import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  FileText, 
  Image, 
  File, 
  Trash2, 
  Download, 
  Search,
  Filter,
  Calendar,
  User,
  FolderOpen,
  Eye,
  RefreshCw,
  Upload,
  X,
  FileImage,
  FileSpreadsheet,
  FileType
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'receipt', label: 'Receipts' },
  { value: 'document', label: 'Documents' },
  { value: 'image', label: 'Images' },
  { value: 'report', label: 'Reports' },
  { value: 'form_attachment', label: 'Form Attachments' },
  { value: 'general', label: 'General' }
];

const getFileIcon = (contentType, filename) => {
  if (contentType?.startsWith('image/')) return <FileImage className="w-5 h-5 text-purple-500" />;
  if (contentType?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
  if (contentType?.includes('spreadsheet') || contentType?.includes('excel') || filename?.endsWith('.xlsx') || filename?.endsWith('.csv')) 
    return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
  if (contentType?.includes('word') || filename?.endsWith('.doc') || filename?.endsWith('.docx')) 
    return <FileText className="w-5 h-5 text-blue-500" />;
  return <File className="w-5 h-5 text-slate-500" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-NG', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const FileManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    category: 'general',
    description: ''
  });
  const [deleting, setDeleting] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchFiles();
  }, [selectedCategory]);

  const fetchFiles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory && selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      params.append('limit', '200');
      
      const response = await axios.get(`${API}/files?${params.toString()}`, getAuthHeader());
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('category', uploadForm.category);
      if (uploadForm.description) {
        formData.append('description', uploadForm.description);
      }

      await axios.post(`${API}/files/upload`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('File uploaded successfully');
      setUploadOpen(false);
      setUploadForm({ file: null, category: 'general', description: '' });
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;
    
    setDeleting(fileId);
    try {
      await axios.delete(`${API}/files/${fileId}`, getAuthHeader());
      toast.success('File deleted');
      fetchFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    } finally {
      setDeleting(null);
    }
  };

  const handlePreview = (file) => {
    setPreviewFile(file);
    setPreviewOpen(true);
  };

  const getDownloadUrl = (file) => {
    return `${API}/files/${file.id}/download?auth=${token}`;
  };

  const filteredFiles = files.filter(file => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      file.original_filename?.toLowerCase().includes(search) ||
      file.uploaded_by_name?.toLowerCase().includes(search) ||
      file.description?.toLowerCase().includes(search) ||
      file.category?.toLowerCase().includes(search)
    );
  });

  // Group files by date
  const groupedFiles = filteredFiles.reduce((groups, file) => {
    const date = file.created_at ? new Date(file.created_at).toLocaleDateString('en-NG') : 'Unknown';
    if (!groups[date]) groups[date] = [];
    groups[date].push(file);
    return groups;
  }, {});

  return (
    <div className="space-y-6" data-testid="file-manager-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            File Manager
          </h1>
          <p className="text-slate-600 mt-1">Browse and manage all uploaded files</p>
        </div>
        <Button 
          onClick={() => setUploadOpen(true)}
          className="bg-emerald-600 hover:bg-emerald-700"
          data-testid="upload-file-btn"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload File
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search files by name, uploader, or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-files-input"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48" data-testid="category-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchFiles} data-testid="refresh-btn">
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <FolderOpen className="w-4 h-4" />
            <span className="text-sm">Total Files</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">{files.length}</div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <FileImage className="w-4 h-4" />
            <span className="text-sm">Images</span>
          </div>
          <div className="text-2xl font-bold text-purple-600">
            {files.filter(f => f.content_type?.startsWith('image/')).length}
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <FileText className="w-4 h-4" />
            <span className="text-sm">Documents</span>
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {files.filter(f => f.content_type?.includes('pdf') || f.content_type?.includes('word') || f.content_type?.includes('document')).length}
          </div>
        </div>
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 text-slate-600 mb-1">
            <FileSpreadsheet className="w-4 h-4" />
            <span className="text-sm">Spreadsheets</span>
          </div>
          <div className="text-2xl font-bold text-green-600">
            {files.filter(f => f.content_type?.includes('spreadsheet') || f.content_type?.includes('excel') || f.original_filename?.endsWith('.xlsx') || f.original_filename?.endsWith('.csv')).length}
          </div>
        </div>
      </div>

      {/* File List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
          <span className="ml-3 text-slate-600">Loading files...</span>
        </div>
      ) : filteredFiles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border">
          <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-600">No Files Found</h3>
          <p className="text-slate-500 mt-1">
            {searchTerm ? 'Try adjusting your search' : 'Upload your first file to get started'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedFiles).map(([date, dateFiles]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-slate-400" />
                <h3 className="text-sm font-medium text-slate-600">{date}</h3>
                <span className="text-xs text-slate-400">({dateFiles.length} files)</span>
              </div>
              <div className="grid gap-3">
                {dateFiles.map((file) => (
                  <div 
                    key={file.id}
                    data-testid={`file-item-${file.id}`}
                    className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                        {getFileIcon(file.content_type, file.original_filename)}
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 truncate">{file.original_filename}</h4>
                        <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                          <span className="bg-slate-100 px-2 py-0.5 rounded">{file.category}</span>
                          <span>{formatFileSize(file.size)}</span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {file.uploaded_by_name}
                          </span>
                        </div>
                        {file.description && (
                          <p className="text-xs text-slate-500 mt-1 truncate">{file.description}</p>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {file.content_type?.startsWith('image/') && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handlePreview(file)}
                            data-testid={`preview-btn-${file.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                        <a
                          href={getDownloadUrl(file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100"
                          data-testid={`download-btn-${file.id}`}
                        >
                          <Download className="w-4 h-4 text-slate-600" />
                        </a>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDelete(file.id)}
                          disabled={deleting === file.id}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-btn-${file.id}`}
                        >
                          {deleting === file.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload File</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpload} className="space-y-4">
            <div>
              <Label htmlFor="file">Select File</Label>
              <Input
                id="file"
                type="file"
                onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                data-testid="file-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Allowed: jpg, png, gif, webp, pdf, doc, docx, xls, xlsx, csv, txt (max 10MB)
              </p>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select 
                value={uploadForm.category} 
                onValueChange={(v) => setUploadForm({ ...uploadForm, category: v })}
              >
                <SelectTrigger data-testid="upload-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="receipt">Receipt</SelectItem>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="image">Image</SelectItem>
                  <SelectItem value="report">Report</SelectItem>
                  <SelectItem value="form_attachment">Form Attachment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Add a description..."
                rows={2}
                data-testid="upload-description"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button 
                type="submit" 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                disabled={uploading || !uploadForm.file}
                data-testid="submit-upload-btn"
              >
                {uploading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
              <Button type="button" variant="outline" onClick={() => setUploadOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileImage className="w-5 h-5" />
              {previewFile?.original_filename}
            </DialogTitle>
          </DialogHeader>
          {previewFile && (
            <div className="space-y-4">
              <div className="flex justify-center bg-slate-100 rounded-lg p-4">
                <img 
                  src={getDownloadUrl(previewFile)} 
                  alt={previewFile.original_filename}
                  className="max-h-96 object-contain rounded"
                />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Size:</span>
                  <span className="ml-2 font-medium">{formatFileSize(previewFile.size)}</span>
                </div>
                <div>
                  <span className="text-slate-500">Type:</span>
                  <span className="ml-2 font-medium">{previewFile.content_type}</span>
                </div>
                <div>
                  <span className="text-slate-500">Uploaded by:</span>
                  <span className="ml-2 font-medium">{previewFile.uploaded_by_name}</span>
                </div>
                <div>
                  <span className="text-slate-500">Date:</span>
                  <span className="ml-2 font-medium">{formatDate(previewFile.created_at)}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <a
                  href={getDownloadUrl(previewFile)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                >
                  <Button className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </a>
                <Button variant="outline" onClick={() => setPreviewOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FileManager;
